import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Airplane } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminLeave() {
  const qc = useQueryClient();
  const { data: pending = [], isLoading } = useQuery({ queryKey: ["admin-leave"], queryFn: async () => (await api.get("/admin/leave")).data });
  const act = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/leave/${id}/status`, { status })).data,
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["admin-leave"] }); toast.success(`Leave ${v.status}`); },
  });

  return (
    <div data-testid="admin-leave">
      <div className="flex items-center gap-2 mb-4">
        <Airplane className="h-5 w-5 text-primary" weight="fill" />
        <h2 className="text-xl font-bold">Leave approvals</h2>
        <Badge variant="secondary" data-testid="admin-leave-count">{pending.length} pending</Badge>
      </div>
      <Card className="p-5">
        {isLoading ? <p className="text-muted-foreground text-sm py-8 text-center">Loading…</p>
          : pending.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center" data-testid="admin-leave-empty">No pending leave requests.</p>
            : (
              <div className="space-y-2" data-testid="admin-leave-list">
                {pending.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`admin-leave-row-${l.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-9 w-1.5 rounded-full shrink-0" style={{ background: l.color }} />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{l.user_name} <span className="text-muted-foreground font-normal">· {l.user_email}</span></p>
                        <p className="text-xs text-muted-foreground">{l.leave_label} · {l.days}d · {l.start_date}{l.end_date !== l.start_date ? ` → ${l.end_date}` : ""}{!l.paid && l.impact ? ` · est. −${money(l.impact)}` : ""}</p>
                        {l.note && <p className="text-xs text-muted-foreground italic mt-0.5">"{l.note}"</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => act.mutate({ id: l.id, status: "approved" })} data-testid={`admin-approve-${l.id}`}><CheckCircle className="h-4 w-4 mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => act.mutate({ id: l.id, status: "declined" })} data-testid={`admin-decline-${l.id}`}><XCircle className="h-4 w-4 mr-1" /> Decline</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </Card>
    </div>
  );
}
